import { api } from "../lib";
import type { QueueSortState } from "../pages/workflows/pathologistReviewQueueView";
import type {
  PendingWorkFilters,
  PendingWorkPage,
  PendingWorkDetailsPage,
  PendingWorkDetail,
} from "../types/pendingWork";

function fetchPendingWorkDetails(rootIds: string[], signal: AbortSignal) {
  return collectPendingWorkDetails({
    rootIds,
    signal,
    cursor: "",
    details: [],
  });
}
async function collectPendingWorkDetails(context: {
  rootIds: string[];
  signal: AbortSignal;
  cursor: string;
  details: PendingWorkDetail[];
}): Promise<PendingWorkDetail[]> {
  const page = await api.get<PendingWorkDetailsPage>("/pending-work/details", {
    params: {
      rootIds: JSON.stringify(context.rootIds),
      cursor: context.cursor,
    },
    signal: context.signal,
  });
  context.details.push(...page.data);
  return page.nextCursor
    ? collectPendingWorkDetails({ ...context, cursor: page.nextCursor })
    : context.details;
}

export function pendingWorkError(error: unknown): string {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 400)
    return "This view has invalid filters or paging. Reset the view and try again.";
  if (status === 401 || status === 403)
    return "Sign in with an active LIS account to view Pending List.";
  return "Pending List could not be loaded. Please try again.";
}
function params(
  filters: PendingWorkFilters,
  sorts: QueueSortState[],
  cursor = ""
) {
  return {
    prefixKeys: JSON.stringify(filters.prefixKeys),
    search: filters.search,
    cause: filters.cause,
    sorts: JSON.stringify(sorts),
    cursor,
  };
}
export const pendingWorkService = {
  detailsBatch: fetchPendingWorkDetails,
  list: (
    filters: PendingWorkFilters,
    sorts: QueueSortState[],
    cursor: string,
    signal: AbortSignal
  ) =>
    api.get<PendingWorkPage>("/pending-work", {
      params: params(filters, sorts, cursor),
      signal,
    }),
  summary: (signal: AbortSignal, filters?: PendingWorkFilters) =>
    api.get<{ total: number; prefixOptions: string[] }>(
      "/pending-work/summary",
      {
        signal,
        params: filters
          ? {
              prefixKeys: JSON.stringify(filters.prefixKeys),
              search: filters.search,
              cause: filters.cause,
            }
          : undefined,
      }
    ),
  details: (rootId: string, cursor: string, signal: AbortSignal) =>
    api.get<PendingWorkDetailsPage>(`/pending-work/${rootId}/details`, {
      params: { cursor },
      signal,
    }),
  exportExcel: (filters: PendingWorkFilters, sorts: QueueSortState[]) =>
    api.get<Blob>("/pending-work/export/excel", {
      params: params(filters, sorts),
      responseType: "blob",
      timeout: 120000,
    }),
};
